'use client'

import { X } from 'lucide-react'
import {
  ROUTE,
  formatTemplate,
  getStopById,
  stopStatusAt,
  type TrackerCopy,
  type TrackerLocale,
  type TrackerState,
} from '@/lib/santa-tracker'
import { formatLocalTime } from './format'

/**
 * Panel de detalle del mapa: la ficha de una ciudad o el estado del trineo.
 *
 * Va **dentro** del contenedor del mapa, anclado abajo, y no ocupa toda la
 * pantalla ni en móvil: una tarjeta a pantalla completa por tocar una ciudad
 * rompe el hilo de la experiencia justo cuando el niño está explorando.
 */

export type TrackerSelection = { kind: 'stop'; stopId: string } | { kind: 'sleigh' } | null

/** El título del panel es su nombre accesible (ver el wrapper). */
const TITLE_ID = 'tracker-detail-title'

const TITLE_CLASS = 'font-christmas text-xl font-bold text-white'

const STATUS_TONE: Record<'visited' | 'current' | 'upcoming', string> = {
  visited: 'text-amber-200',
  current: 'text-white',
  upcoming: 'text-sky-200',
}

function StopDetail({
  stopId,
  copy,
  locale,
  state,
}: {
  stopId: string
  copy: TrackerCopy
  locale: TrackerLocale
  state: TrackerState
}) {
  const stop = getStopById(stopId)
  if (!stop) return null
  const status = stopStatusAt(stop, state)

  return (
    <>
      <p id={TITLE_ID} className={TITLE_CLASS}>
        {copy.stops[stop.id]}
      </p>
      <p className={`mt-1 text-sm ${STATUS_TONE[status]}`}>{copy.map.stopStatus[status]}</p>
      <p className="mt-2 text-sm tabular-nums text-white/75">
        {formatTemplate(copy.map.localTime, {
          time: formatLocalTime(state.nowMs, stop.utcOffset, locale),
        })}
      </p>
    </>
  )
}

function SleighDetail({
  copy,
  locale,
  state,
  statusMessage,
}: {
  copy: TrackerCopy
  locale: TrackerLocale
  state: TrackerState
  statusMessage: string
}) {
  const lastStop = state.lastStopId ? getStopById(state.lastStopId) : null
  const nextStop = state.nextStopId ? getStopById(state.nextStopId) : null

  return (
    <>
      <p id={TITLE_ID} className={TITLE_CLASS}>
        {copy.map.sleighLabel}
      </p>
      <p className="mt-1 text-sm text-white/80">{statusMessage}</p>
      <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-white/60">{copy.status.lastVisited}</dt>
          <dd className="text-white">
            {lastStop && lastStop.leg !== 'north-pole'
              ? copy.stops[lastStop.id]
              : copy.status.atNorthPole}
          </dd>
        </div>
        <div>
          <dt className="text-white/60">{copy.status.nextStop}</dt>
          <dd className="text-white">
            {nextStop && nextStop.id !== ROUTE[ROUTE.length - 1].id
              ? copy.stops[nextStop.id]
              : copy.status.backHome}
          </dd>
        </div>
      </dl>
      {nextStop && (
        <p className="mt-2 text-xs tabular-nums text-white/60">
          {formatTemplate(copy.map.localTime, {
            time: formatLocalTime(state.nowMs, nextStop.utcOffset, locale),
          })}
        </p>
      )}
    </>
  )
}

export interface TrackerDetailPanelProps {
  selection: TrackerSelection
  copy: TrackerCopy
  locale: TrackerLocale
  state: TrackerState
  statusMessage: string
  onClose: () => void
}

export function TrackerDetailPanel({
  selection,
  copy,
  locale,
  state,
  statusMessage,
  onClose,
}: TrackerDetailPanelProps) {
  if (!selection) return null

  // Un `<section>` con nombre, no una modal: no atrapa el foco, no bloquea el
  // resto de la página y se puede dejar abierto mientras el trineo sigue
  // volando — que es justo lo que se quiere cuando un niño va tocando ciudades.
  // Se nombra con su propio título (la ciudad o el trineo) y el `aria-live`
  // hace que el lector anuncie el cambio al elegir otra.
  return (
    <section
      aria-labelledby={TITLE_ID}
      aria-live="polite"
      className="absolute inset-x-3 bottom-3 z-10 max-w-sm rounded-2xl border border-white/20 bg-slate-950/85 p-4 shadow-xl backdrop-blur-md sm:inset-x-auto sm:left-4 sm:bottom-4"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={copy.map.close}
        className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full text-white/70 transition hover:bg-white/10 hover:text-white"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
      <div className="pr-8">
        {selection.kind === 'stop' ? (
          <StopDetail stopId={selection.stopId} copy={copy} locale={locale} state={state} />
        ) : (
          <SleighDetail
            copy={copy}
            locale={locale}
            state={state}
            statusMessage={statusMessage}
          />
        )}
      </div>
    </section>
  )
}
